/**
 * BusinessDataDialog — Edición de los datos del negocio (nombre, perfil, zona horaria, moneda).
 *
 * WHY: el botón EDITAR del banner "Perfil activo" llevaba a /onboarding, que al terminar
 * ejecuta POST /onboarding/complete → SaveOnboardingQuery hace destroy+create de
 * Professionals y ServicesCatalog. Con las FK ON DELETE SET NULL, eso vaciaba el
 * professionalId/serviceId de todas las citas y perdía Professional.whatsappUserId.
 * Este diálogo guarda con PATCH /onboarding/config, que solo hace UPDATE.
 *
 * El resto de la configuración (servicios, equipo, horarios, secuencias) ya se edita de
 * forma granular y no destructiva desde las secciones de esta misma pestaña.
 */
import React, { useEffect, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import { BUSINESS_PROFILES, TIMEZONES, CURRENCIES } from "./appointments.config.constants";
import { i18n } from "../../translate/i18n";

export default function BusinessDataDialog({ open, data, saving, onClose, onSave }) {
  const [form, setForm] = useState(data);

  useEffect(() => {
    if (open) setForm(data);
  }, [open, data]);

  const change = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleProfileChange = (e) => {
    const id = Number(e.target.value);
    const profile = BUSINESS_PROFILES.find((p) => p.id === id);
    setForm((f) => ({ ...f, profileId: id, profileName: profile ? profile.name : f.profileName }));
  };

  const canSave = Boolean(form.businessName && form.businessName.trim()) && form.profileId != null && !saving;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{i18n.t("appointmentsModule.config.businessDialogTitle")}</DialogTitle>
      <DialogContent>
        <DialogContentText variant="body2">
          {i18n.t("appointmentsModule.config.businessDialogDesc")}
        </DialogContentText>

        <TextField
          label={i18n.t("appointmentsModule.config.businessDialogName")}
          value={form.businessName || ""}
          onChange={change("businessName")}
          fullWidth
          required
          variant="outlined"
          margin="normal"
        />

        <TextField
          select
          label={i18n.t("appointmentsModule.config.businessDialogProfile")}
          value={form.profileId ?? ""}
          onChange={handleProfileChange}
          fullWidth
          required
          variant="outlined"
          margin="normal"
          helperText={i18n.t("appointmentsModule.config.businessDialogProfileHelper")}
        >
          {BUSINESS_PROFILES.map((p) => (
            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label={i18n.t("appointmentsModule.config.businessDialogTimezone")}
          value={form.timezone || "America/Lima"}
          onChange={change("timezone")}
          fullWidth
          variant="outlined"
          margin="normal"
        >
          {TIMEZONES.map((tz) => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}
        </TextField>

        <TextField
          select
          label={i18n.t("appointmentsModule.config.businessDialogCurrency")}
          value={form.currency || "PEN"}
          onChange={change("currency")}
          fullWidth
          variant="outlined"
          margin="normal"
        >
          {CURRENCIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {i18n.t("appointmentsModule.config.businessDialogCancel")}
        </Button>
        <Button onClick={() => onSave(form)} color="primary" variant="contained" disabled={!canSave}>
          {saving
            ? i18n.t("appointmentsModule.config.businessDialogSaving")
            : i18n.t("appointmentsModule.config.businessDialogSave")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
